import React, { createContext, useContext, useState } from "react";
import { ERROR, LOADING, SUCCESS } from "../constants/common";
import { useAlert } from "./Alert";
import {
  addComment,
  addPost,
  editPost,
  deletePost,
  getPosts,
  getSuggestedPeople,
  likePost,
  updateCurrentStatus,
  addReply,
  getRepliesOfComment,
  likeComment,
  likeReply,
  unlikeReply,
  unlikeComment,
  editComment,
  editReply,
  getPost,
  searchPosts,
  getSummaryPosts,
  getMarketingData,
} from "../services/home.services";
import CreatePostModal from "../components/CreatePostModal";
import { ReactComponent as WarningIcon } from "../assets/Icons/warning.svg";
import scrollTo from "../helper/scrollTo";
import { useAuth } from "./Auth";

const HomePageContext = createContext({});

export const HomePageContextProvider = ({ children }) => {
  const { sendAlert } = useAlert();
  const { account_type } = useAuth();
  const [singlePost, setSinglePost] = useState(null);

  // State
  const [homePageState, setHomePageState] = useState({
    fetchStatus: "",
    suggestPeople: [],
    createPostStatus: "",
    posts: {
      data: [],
    },
    searchedPosts: {
      data: [],
    },
    fetchPostsStatus: "",
    fetchSearchPostsStatus: "",
    createCommentStatus: "",
    addReplayStatus: "",
    likePostStatus: "",
    likeCommentStatus: "",
    currentStatus: {},
    updateCurrentStatusStatus: "",
    postdata: null,
    repliesToComment: [],
    updateCommentStatus: "",
    editReplayStatus: "",
    searchQuery: "",
    searchResults: [],
    isSearching: false,
    searchError: null,
    searchTotal: 0,
    searchPage: 1,
    searchLimit: 15,
  });

  // State management functions
  const handleState = (obj) => {
    setHomePageState((prevState) => ({
      ...prevState,
      ...obj,
    }));
  };

  const updateHomePagetate = (field, value) => {
    handleState({ [field]: value });
  };

  const updateEditPostData = (data) => {
    setHomePageState((prevState) => ({
      ...prevState,
      postdata: data,
    }));
  };

  const [openCreatePostModal, setOpenCreatePostModal] = useState(false);

  const handleOpenCreatePostModal = () => {
    setOpenCreatePostModal(true);
  };

  const handleCloseCreatePostModal = () => {
    setHomePageState((prev) => ({ ...prev, postdata: null }));
    setOpenCreatePostModal(false);
  };

  async function fetchSuggestedPeople(data) {
    handleState({ fetchSuggestPeopleStatus: LOADING });
    try {
      const response = await getSuggestedPeople(data);
      handleState({
        suggestPeople: response.data.data,
        fetchSuggestPeopleStatus: SUCCESS,
      });
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ fetchSuggestPeopleStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function fetchPosts(params) {
    handleState({ fetchPostsStatus: LOADING });
    try {
      const response = await getPosts(params);
      handleState({
        posts:
          params.page > 1
            ? {
                ...response.data.data,
                data: [...homePageState.posts.data, ...response.data.data.data],
              }
            : response.data.data,
        fetchPostsStatus: SUCCESS,
      });
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ fetchPostsStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function fetchSummaryPosts(params) {
    handleState({ fetchPostsStatus: LOADING });
    try {
      const response = await getSummaryPosts(params);
      handleState({
        posts:
          params.page > 1
            ? {
                ...response.data.data,
                data: [...homePageState.posts.data, ...response.data.data.data],
              }
            : response.data.data,
        fetchPostsStatus: SUCCESS,
      });
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ fetchPostsStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function fetchMarketingData(params) {
    handleState({ fetchPostsStatus: LOADING });
    try {
      const response = await getMarketingData(params);
      handleState({
        marketingData: response.data,
        fetchPostsStatus: SUCCESS,
      });
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ fetchPostsStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function handleSearch(query, page = 1) {
    if (!query.trim()) {
      handleState({
        searchResults: [],
        isSearching: false,
        searchTotal: 0,
        searchPage: 1,
      });
      return;
    }

    handleState({
      isSearching: true,
      searchError: null,
      searchPage: page,
    });

    try {
      const response = await searchPosts({
        query,
        page,
        limit: homePageState.searchLimit,
      });
      handleState({
        searchResults:
          page === 1
            ? response.data.data.data
            : [...homePageState.searchResults, ...response.data.data.data],
        searchTotal: response.data.data.total,
        isSearching: false,
      });
    } catch (error) {
      handleState({
        searchResults: [],
        isSearching: false,
        searchError:
          error.response?.data?.message || "An error occurred while searching",
      });
      Promise.reject(error);
    }
  }

  const fetchSinglePost = async ({
    account_type,
    post_id,
    comment_id = null,
  }) => {
    if (!account_type || !post_id) {
      console.warn(
        "fetchSinglePost called with invalid params:",
        account_type,
        post_id
      );
      return;
    }
    handleState({ fetchPostsStatus: LOADING });

    try {
      const res = await getPost({ account_type, post_id });
      const post = res.data.data;

      if (comment_id) {
        const comment = post.comments.find((c) => c.id == comment_id);
        if (comment) {
          comment.user.connected = comment.connected;
        }
      }

      setSinglePost(post);
      handleState({
        posts: {
          ...homePageState.posts,
          data: [
            post,
            ...homePageState.posts.data.filter((item) => item.id !== post.id),
          ],
        },
        fetchPostsStatus: SUCCESS,
      });
    } catch (error) {
      console.error("Error fetching single post:", error);
      handleState({ fetchPostsStatus: ERROR });
    }
  };

  async function createPost(data) {
    handleState({ createPostStatus: LOADING });
    try {
      const response = await addPost(data);
      if (response.data?.type === "warning") {
        sendAlert(response.data?.data?.message, "warning", true, null, WarningIcon);
        handleState({ createPostStatus: ERROR });
        return;
      }
      await fetchPosts({ page: 1, account_type });
      handleState({
        createPostStatus: SUCCESS,
      });
      sendAlert("The post has been created successfully", "success");
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ createPostStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function editPostAction(data) {
    handleState({ createPostStatus: LOADING });
    try {
      const response = await editPost(homePageState.postdata.id, data);
      if (response.data?.type === "warning") {
        sendAlert(response.data?.data?.message, "warning", true, null, WarningIcon);
        handleState({ createPostStatus: ERROR });
        return;
      }
      await fetchPosts({ page: 1, account_type });
      handleState({
        createPostStatus: SUCCESS,
      });
      sendAlert(
        "The post has been updated successfully",
        "success"
      );
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ createPostStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function togglePostLike(data) {
    handleState({ likePostStatus: LOADING });
    try {
      const response = await likePost(data);
      handleState({
        likePostStatus: SUCCESS,
        posts: {
          ...homePageState.posts,
          data: homePageState.posts.data.map((item) => {
            if (item.id === data.post_id) {
              return {
                ...item,
                liked: data.isLiked,
                likes_count: data.isLiked
                  ? ++item.likes_count
                  : --item.likes_count,
              };
            }
            return item;
          }),
        },
      });
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ likePostStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function handleLikeComment(data, post_id) {
    handleState({ likeCommentStatus: LOADING });
    try {
      const response = await likeComment(data);
      handleState({
        likeCommentStatus: SUCCESS,
        posts: {
          ...homePageState.posts,
          data: homePageState.posts.data.map((item) => {
            if (item.id === post_id) {
              return {
                ...item,
                comments: item.comments.map((comment) => {
                  if (comment.id === data.comment_id) {
                    return {
                      ...comment,
                      liked: true,
                      likes_count: response?.data?.data?.likes,
                    };
                  }
                  return comment;
                }),
              };
            }
            return item;
          }),
        },
      });
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ likeCommentStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function handleUnlikeComment(data, post_id) {
    handleState({ likeCommentStatus: LOADING });
    try {
      const response = await unlikeComment(data);
      handleState({
        likeCommentStatus: SUCCESS,
        posts: {
          ...homePageState.posts,
          data: homePageState.posts.data.map((item) => {
            if (item.id === post_id) {
              return {
                ...item,
                comments: item.comments.map((comment) => {
                  if (comment.id === data.comment_id) {
                    return {
                      ...comment,
                      liked: false,
                      likes_count: response?.data?.data?.likes,
                    };
                  }
                  return comment;
                }),
              };
            }
            return item;
          }),
        },
      });
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ likeCommentStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function handleLikeReply(data, { comment_id, post_id }) {
    handleState({ likeCommentStatus: LOADING });
    try {
      const response = await likeReply(data);
      handleState({
        likeCommentStatus: SUCCESS,
        posts: {
          ...homePageState.posts,
          data: homePageState.posts.data.map((item) => {
            if (item.id === post_id) {
              return {
                ...item,
                comments: item.comments.map((comment) => {
                  if (comment.id === comment_id) {
                    return {
                      ...comment,
                      replies: comment.replies.map((r) => {
                        if (r.id === response?.data?.data?.id) {
                          return {
                            ...r,
                            likes_count: response?.data?.data?.likes,
                            liked: true,
                          };
                        } else {
                          return r;
                        }
                      }),
                    };
                  }
                  return comment;
                }),
              };
            }
            return item;
          }),
        },
      });
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ likeCommentStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function handleUnLikeReply(data, { comment_id, post_id }) {
    handleState({ likeCommentStatus: LOADING });
    try {
      const response = await unlikeReply(data);
      handleState({
        likeCommentStatus: SUCCESS,
        posts: {
          ...homePageState.posts,
          data: homePageState.posts.data.map((item) => {
            if (item.id === post_id) {
              return {
                ...item,
                comments: item.comments.map((comment) => {
                  if (comment.id === comment_id) {
                    return {
                      ...comment,
                      replies: comment.replies.map((r) => {
                        if (r.id === response?.data?.data?.id) {
                          return {
                            ...r,
                            likes_count: response?.data?.data?.likes,
                            liked: false,
                          };
                        } else {
                          return r;
                        }
                      }),
                    };
                  }
                  return comment;
                }),
              };
            }
            return item;
          }),
        },
      });
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ likeCommentStatus: ERROR });
      Promise.reject(error);
    }
  }

  const deletePostAction = (id) => {
    return deletePost(id);
  };

  async function createComment(data) {
    handleState({ createCommentStatus: LOADING });
    try {
      const response = await addComment(data);
      if (response.data?.type === "warning") {
        sendAlert(response.data?.data?.message, "warning", true, null, WarningIcon);
        handleState({ createCommentStatus: ERROR });
        return;
      }
      handleState({
        createCommentStatus: SUCCESS,
        posts: {
          ...homePageState.posts,
          data: homePageState.posts.data.map((item) => {
            if (item.id === data.post_id) {
              return {
                ...item,
                comments: [...item.comments, response.data.data],
              };
            }
            return item;
          }),
        },
      });
      sendAlert("The Comment has been created successfully", "success");
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ createCommentStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function updateComment(data, post_id) {
    handleState({ updateCommentStatus: LOADING });
    try {
      const response = await editComment(data);
      if (response.data?.type === "warning") {
        sendAlert(response.data?.data?.message, "warning", true, null, WarningIcon);
        handleState({ updateCommentStatus: ERROR });
        return;
      }
      handleState({
        updateCommentStatus: SUCCESS,
        posts: {
          ...homePageState.posts,
          data: homePageState.posts.data.map((item) => {
            if (item.id === post_id) {
              return {
                ...item,
                comments: item.comments.map((comment) => {
                  if (comment.id === data.comment_id) {
                    return {
                      ...comment,
                      comment: data.comment,
                    };
                  }
                  return comment;
                }),
              };
            }
            return item;
          }),
        },
      });
      sendAlert("The Comment has been updated successfully", "success");
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ updateCommentStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function addReplyHandler(data, post_id) {
    handleState({ addReplayStatus: LOADING });
    try {
      const response = await addReply(data);
      if (response.data?.type === "warning") {
        sendAlert(response.data?.data?.message, "warning", true, null, WarningIcon);
        handleState({ addReplayStatus: ERROR });
        return;
      }
      handleState({
        addReplayStatus: SUCCESS,
        posts: {
          ...homePageState.posts,
          data: homePageState.posts.data.map((item) => {
            if (item.id === post_id) {
              return {
                ...item,
                comments: item.comments.map((comment) => {
                  if (comment.id === data.comment_id) {
                    return {
                      ...comment,
                      replies_count: comment.replies_count + 1,
                      replies: [
                        response.data.data,
                        ...(comment?.replies || []),
                      ],
                    };
                  }
                  return comment;
                }),
              };
            }
            return item;
          }),
        },
      });
      scrollTo(document.getElementById(`comment-${data.comment_id}`), 100);
      sendAlert("The Reply Comment has been created successfully", "success");
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ addReplayStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function updateReplyHandler(data, { post_id, comment_id }) {
    handleState({ editReplayStatus: LOADING });
    try {
      const response = await editReply(data);
      if (response.data?.type === "warning") {
        sendAlert(response.data?.data?.message, "warning", true, null, WarningIcon);
        handleState({ editReplayStatus: ERROR });
        return;
      }
      handleState({
        editReplayStatus: SUCCESS,
        posts: {
          ...homePageState.posts,
          data: homePageState.posts.data.map((item) => {
            if (item.id === post_id) {
              return {
                ...item,
                comments: item.comments.map((comment) => {
                  if (comment.id === comment_id) {
                    return {
                      ...comment,
                      replies: comment?.replies.map((reply) => {
                        if (reply.id === data.reply_id) {
                          return {
                            ...reply,
                            reply: data.reply,
                          };
                        }
                        return reply;
                      }),
                    };
                  }
                  return comment;
                }),
              };
            }
            return item;
          }),
        },
      });
      sendAlert("The Reply Comment has been created successfully", "success");
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ editReplayStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function getRepliesOfCommentHandler(params, post_id) {
    try {
      const response = await getRepliesOfComment(params);
      handleState({
        createCommentStatus: SUCCESS,
        posts: {
          ...homePageState.posts,
          data: homePageState.posts.data.map((item) => {
            if (item.id === post_id) {
              return {
                ...item,
                comments: item.comments.map((comment) => {
                  if (comment.id === params) {
                    return {
                      ...comment,
                      replies: response.data.data,
                    };
                  }
                  return comment;
                }),
              };
            }
            return item;
          }),
        },
      });
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  }

  async function editCurrentStatus(data) {
    handleState({ updateCurrentStatusStatus: LOADING });
    try {
      const response = await updateCurrentStatus(data);
      handleState({
        updateCurrentStatusStatus: SUCCESS,
      });
      sendAlert("Thanks for sharing your availability! This helps others stay up to date.", "success");
      return response;
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
      handleState({ updateCurrentStatusStatus: ERROR });
      Promise.reject(error);
    }
  }

  return (
    <HomePageContext.Provider
      value={{
        // State
        ...homePageState,
        // Handlers
        fetchSuggestedPeople,
        createPost,
        fetchPosts,
        fetchSummaryPosts,
        fetchSinglePost,
        createComment,
        updateComment,
        togglePostLike,
        handleOpenCreatePostModal,
        handleCloseCreatePostModal,
        editCurrentStatus,
        updateHomePagetate,
        updateEditPostData,
        deletePostAction,
        editPostAction,
        addReplyHandler,
        updateReplyHandler,
        getRepliesOfCommentHandler,
        handleLikeComment,
        handleUnlikeComment,
        handleLikeReply,
        handleUnLikeReply,
        handleSearch,
        handleState,
        fetchMarketingData,
      }}
    >
      {children}
      <CreatePostModal
        open={openCreatePostModal}
        onClose={handleCloseCreatePostModal}
        postData={homePageState.postdata}
        isEditMode={!!homePageState.postdata}
      />
    </HomePageContext.Provider>
  );
};

export const useHomePage = () => useContext(HomePageContext);
